import Link from 'next/link';
import { buildClientDetailHref } from '@/app/(main)/clients/utils/buildClientDetailHref';
import { Avatar } from '@/app/components/Avatar';
import { buttonStyles } from '@/app/components/buttons/BaseButton/styles';
import type { DuplicateClientMatch } from '@/types/clientsApiTypes';
import { cn } from '@/utils/сn';
import type { DuplicateHighlightField } from '../../utils/getDuplicateWarningContent';
import ChevronRightIcon from '/public/icons/arrow-right.svg';

interface DuplicateClientCardProps {
  client: DuplicateClientMatch;
  highlightField: DuplicateHighlightField;
}

function HighlightedValue({ value, isHighlighted }: { value: string; isHighlighted: boolean }) {
  return (
    <span className={cn(isHighlighted && 'rounded-md bg-red-500/10 text-red-500 px-0.5 py-0.5 break-words')}>
      {value}
    </span>
  );
}

export function DuplicateClientCard({ client, highlightField }: DuplicateClientCardProps) {
  const profilePictureUrl = client.profile_picture_url ?? undefined;
  const highlightName = highlightField === 'name' || highlightField === 'both';
  const highlightEmail = highlightField === 'email' || highlightField === 'both';

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-grey-200 p-4 max-md:flex-col max-md:items-stretch max-md:gap-4">
      <div className="flex min-w-0 items-center gap-2 max-md:items-start">
        <Avatar name={client.full_name} imageUrl={profilePictureUrl} className="self-start" />

        <div className="min-w-0 text-left gap-0.5 flex flex-col">
          <p className="text-body-regular text-black max-md:text-sm max-md:leading-[150%]">
            <HighlightedValue value={client.full_name} isHighlighted={highlightName} />
          </p>
          <p className="text-button-regular text-grey-700">
            <HighlightedValue value={client.email} isHighlighted={highlightEmail} />
          </p>
          <p className="text-button-regular text-grey-700 mt-1.5 max-md:mt-2">
            <span className="text-grey-700">Agent: </span>
            <span className="text-blue-900">{client.agent?.name ?? '—'}</span>
          </p>
        </div>
      </div>

      <Link
        href={buildClientDetailHref(client.id)}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          buttonStyles({ intent: 'secondary', size: 'full_short' }),
          'shrink-0 whitespace-nowrap w-36.5 h-9 text-sm font-medium [&>svg]:h-4 [&>svg]:w-4 max-md:w-full'
        )}
      >
        View Profile
        <ChevronRightIcon viewBox="0 0 18 18" className="[&_path]:fill-current" aria-hidden />
      </Link>
    </div>
  );
}
